import { FormikProvider, useFormik } from "formik";
import React, { useEffect } from "react";
import TextInput from "./FormInputs/TextInput2";
import { Button } from "./Button/Button";
import { useMutation } from "react-query";
import { KycServices } from "../services/kyc";
import { AuthActions, useAuth } from "../zustand/auth.store";
import Modal from "./Modal/Modal";

import * as Yup from "yup";
import PageLoader from "./spinner/PageLoader";
import AddressAutocomplete from "./FormInputs/AutoComplete";
import { ProductContext } from "../context/ProductContext";
import toast from "react-hot-toast";

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required("Name is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "Name should only contain letters, numbers, and spaces"
    )
    .min(3, "Name should be at least 3 characters long"),
  lastName: Yup.string()
    .required("Name is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "Name should only contain letters, numbers, and spaces"
    )
    .min(3, "Name should be at least 3 characters long"),

  email_address: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),

  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(/^\d+$/, "Phone number should only contain digits")
    .min(10, "Phone number should be at least 10 digits")
    .max(15, "Phone number should not exceed 15 digits"),

  homeAddress: Yup.string()
    .required("Home address is required")
    .min(5, "Home address should be at least 5 characters long"),

  city: Yup.string()
    .required("City is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "City should only contain letters, numbers, and spaces"
    )
    .min(3, "Province should be at least 3 characters long"),

  state: Yup.string()
    .required("Province is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "Please enter the full province name (e.g., Ontario)"
    )
    .min(3, "Please enter the complete province name"),

  zipCode: Yup.string()
    .required("Posstal code is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "Postal code should only contain letters, numbers"
    )
    .length(6, "Postal code should be exactly 6 characters"),

  jobTitle: Yup.string()
    .required("Job title is required")
    .min(2, "Job title should be at least 2 characters long"),

  employerName: Yup.string()
    .required("Employer name is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "Employer name should only contain letters, numbers, and spaces"
    )
    .min(2, "Employer name should be at least 2 characters long"),

  employerPhoneNumber: Yup.string()
    .required("Employer phone number is required")
    .matches(/^\d+$/, "Employer phone number should only contain digits")
    .min(10, "Employer phone number should be at least 10 digits")
    .max(15, "Employer phone number should not exceed 15 digits"),

  others: Yup.string()
    .required("field is required")
    .nullable() // Optional field, can be null
    .max(500, "Others field should not exceed 500 characters"),
});

const Kyc = () => {
  const [isKycLoading, setIsKycLoading] = React.useState(false);
  const product: any = React.useContext(ProductContext);
  const [pageLoading, setPageLoading] = React.useState(true);
  const [verificationUrl, setVerificationUrl] = React.useState(null);
  const [verificationRef, setVerificationRef] = React.useState(null);
  const [verificationStatus, setVerificationStatus] = React.useState<any>(null);
  const [verificationMessage, setVerificationMessage] =
    React.useState<any>(null);
  const [pollingTimeout, setPollingTimeout] =
    React.useState<NodeJS.Timeout | null>(null);
  const [pollingAttempts, setPollingAttempts] = React.useState(0);
  const profile = useAuth((s) => s.profile);

  // console.log(profile.email_address)
  const form = useFormik({
    initialValues: {
      firstName: profile.firstName ?? "",
      lastName: profile.lastName ?? "",
      email_address: profile.email_address ?? "",
      phoneNumber: profile.phoneNumber ?? "",
      homeAddress: profile.homeAddress ?? "",
      city: profile.city ?? "",
      state: profile.state ?? "",
      zipCode: profile.zipCode ?? "",
      jobTitle: profile.jobTitle ?? "",
      employerName: profile.employerName ?? "",
      employerPhoneNumber: profile.employerPhoneNumber ?? "",
      others: "",
      other: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values: any) => {
      // console.log(values)
      handleSubmit.mutate(values);
    },
  });

  useEffect(() => {
    initialStart();

    // Cleanup function to stop polling when component unmounts
    return () => {
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
        setPollingTimeout(null);
      }
    };
  }, []);

  const initialStart = async () => {
    try {
      if (profile.kycVerificationReference) {
        const callBackBody = {
          reference: profile.kycVerificationReference,
        };

        const res = await callbackFunc(callBackBody);
      }
      const kycres = await KycServices.getKyc(profile.id);

      if (kycres.data) {
        form.setValues(kycres.data);
        // setIsModalOpen(true)
        setVerificationMessage(true);
      }
      // console.log(kycres)
    } catch (error) {
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubmit = useMutation(
    async (values: any) => {
      const payload = {
        ...values,
        ...(values.others === "other"
          ? { others: values.other }
          : { others: values.others }),
      };
      delete payload.other;
      return await KycServices.addKyc(payload, profile.id);
    },
    {
      onSuccess: (data) => {
        // setIsModalOpen(true)
        setVerificationMessage(true);
      },
      onError: (err: any) => {
        if (err.response.status === 400) {
          // setIsModalOpen(true)
          setVerificationMessage(true);
        } else {
          toast.error(
            err.response.data.message ||
              "An error occurred while submitting your KYC information. Please try again later."
          );
        }
      },
    }
  );

  const initiateKyc = async () => {
    setIsKycLoading(true);
    try {
      const body = {
        email: profile.email_address,
        face: {},
        document: {},
      };
      const initiateRes = await KycServices.initializeKyc(body);
      setVerificationUrl(initiateRes.data.verification_url);
      setVerificationRef(initiateRes.data.reference);
      setVerificationMessage(false);

      // Open verification URL in a new window instead of iframe
      const newWindow = window.open(
        initiateRes.data.verification_url,
        "_blank",
        "width=800,height=600,scrollbars=yes,resizable=yes"
      );

      if (newWindow) {
        // Set up a polling mechanism to check verification status
        const checkVerificationStatus = async () => {
          try {
            const callBackBody = {
              reference: initiateRes.data.reference,
            };
            const callBackRes = await callbackFunc(callBackBody);

            if (callBackRes.message === "Verification request successful") {
              // Stop polling on success
              if (pollingTimeout) {
                clearTimeout(pollingTimeout);
                setPollingTimeout(null);
              }
              setVerificationStatus("successful");
              setVerificationMessage(true);
              setVerificationUrl(null);
              newWindow.close();
              return;
            } else if (
              callBackRes.message === "Verification request is pending"
            ) {
              // Check if we've exceeded max attempts (10 minutes = 120 attempts)
              if (pollingAttempts >= 120) {
                toast.error("Verification timeout. Please try again.");
                setVerificationUrl(null);
                setVerificationMessage(true);
                return;
              }

              // Continue polling if verification is still pending
              setPollingAttempts((prev) => prev + 1);
              const timeout = setTimeout(checkVerificationStatus, 5000);
              setPollingTimeout(timeout);
            } else {
              // Handle other status messages
              console.log("Verification status:", callBackRes.message);
              setPollingAttempts((prev) => prev + 1);
              const timeout = setTimeout(checkVerificationStatus, 5000);
              setPollingTimeout(timeout);
            }
          } catch (error) {
            console.error("Error checking verification status:", error);
            // Continue polling even if there's an error, but with longer interval
            setPollingAttempts((prev) => prev + 1);
            const timeout = setTimeout(checkVerificationStatus, 10000);
            setPollingTimeout(timeout);
          }
        };

        // Start polling after a short delay
        setTimeout(checkVerificationStatus, 3000);
      } else {
        // Fallback: if popup is blocked, show instructions to user
        toast.error(
          "Please allow popups for this site to complete KYC verification"
        );
        setVerificationMessage(true);
      }
    } catch (err) {
      toast.error("Failed to initiate KYC verification. Please try again.");
    } finally {
      setIsKycLoading(false);
    }
  };

  const callbackFunc = async (data: any) => {
    return await KycServices.kycCallbac(data);
  };

  const closeIframe = async () => {
    try {
      const callBackBody = {
        reference: verificationRef,
      };

      const res = await callbackFunc(callBackBody);

      if (res.message === "Verification request successful") {
        setVerificationStatus("successful");
        setVerificationMessage(true);
        setVerificationUrl(null);
      } else if (res.message === "Verification request is pending") {
        toast.loading(
          "Verification is still in progress. Please complete the verification process in the new window.",
          {
            duration: 10000,
          }
        );
        // Don't close the verification URL yet, let user continue
      } else {
        console.log("Verification status:", res.message);
        setVerificationUrl(null);
      }
    } catch (error) {
      setVerificationUrl(null);
      toast.error("Failed to complete KYC verification. Please try again.");
    }
  };

  const openVerificationInNewWindow = () => {
    if (verificationUrl) {
      const newWindow = window.open(
        verificationUrl,
        "_blank",
        "width=800,height=600,scrollbars=yes,resizable=yes"
      );
      if (!newWindow) {
        toast.error(
          "Please allow popups for this site to complete KYC verification"
        );
      }
    }
  };

  return (
    <Modal showCloseButton={false} open={product.isKycOpen}>
      <div className="max-h-[90vh] overflow-y-auto">
        {pageLoading ? (
          <PageLoader />
        ) : (
          <>
            {verificationMessage ? (
              verificationStatus ? (
                verificationStatus === "successful" ? (
                  <div className="w-[90vw] flex flex-col items-center gap-6 md:!w-[600px]  h-[450px]">
                    <img src="/face-id.png" alt="" />
                    <h3 className="font-semibold text-center text-2xl">
                      KYC Completed
                    </h3>
                    <h5 className="text-sm text-center  max-w-[318px] text-[#464749]">
                      Verification complete! You're one step closer to financial
                      freedom.
                    </h5>
                    <Button
                      label="Proceed"
                      disabled={isKycLoading}
                      onClick={() => AuthActions.setVerified(true)}
                      isLoading={isKycLoading}
                      className="mt-6 w-full text-center flex font-semibold justify-center"
                    />
                  </div>
                ) : null
              ) : (
                <div className="w-[90vw] flex flex-col items-center gap-6 md:!w-[600px]  h-[450px]">
                  <img src="/success.svg" alt="" />
                  <h3 className="font-semibold text-center text-2xl">
                    Personal Information Updated
                  </h3>
                  <h5 className="text-sm text-center  max-w-[318px] text-[#464749]">
                    You have provided your personal information, kindly proceed
                    to your KYC.
                  </h5>
                  <Button
                    label="Proceed to KYC"
                    disabled={isKycLoading}
                    onClick={() => initiateKyc()}
                    isLoading={isKycLoading}
                    className="mt-6 w-full text-center flex font-semibold justify-center"
                  />
                </div>
              )
            ) : verificationUrl ? (
              // Show instructions instead of iframe
              <div className="w-[90vw] flex flex-col items-center gap-6 md:!w-[600px] h-[450px]">
                <img src="/face-id.png" alt="" />
                <h3 className="font-semibold text-center text-2xl">
                  KYC Verification
                </h3>
                <h5 className="text-sm text-center max-w-[318px] text-[#464749]">
                  A new window will open for KYC verification. Please complete
                  the verification process in the new window.
                </h5>
                <div className="flex gap-3 mt-4">
                  <Button
                    label="Verify Now"
                    onClick={openVerificationInNewWindow}
                    className="px-5"
                  />
                  <Button
                    label="Continue"
                    variant="outlined"
                    onClick={closeIframe}
                    className="px-5"
                  />
                </div>
              </div>
            ) : (
              <div className="max-w-[513px] overflow-y-auto rounded-[10px] w-full bg-white mt-9 border border-[#19124A] py-6 px-5 ">
                <h3 className="md:text-3xl text-xl font-medium">
                  Complete Personal Information
                </h3>

                <FormikProvider value={form}>
                  <form
                    className="mt-6 overflow-y-auto"
                    onSubmit={form.handleSubmit}
                  >
                    <div className="rounded-[10px] border p-4">
                      <h3 className="text-base md:text-lg font-semibold">
                        Personal Information
                      </h3>
                      <div className="mt-3">
                        <div className="grid grid-cols-2 gap-2">
                          <TextInput
                            name="firstName"
                            label="First Name *"
                            placeholder="Enter your first name"
                          />
                          <TextInput
                            name="lastName"
                            label="Last Name *"
                            placeholder="Enter your last name"
                          />
                        </div>
                        <TextInput
                          name="email_address"
                          label="Email *"
                          wrapperClass="mt-3"
                          placeholder="Enter your email"
                        />
                        <TextInput
                          name="phoneNumber"
                          label="Phone Number *"
                          wrapperClass="mt-3"
                          placeholder="Enter your phone number"
                        />

                        <div className="mt-3">
                          <label>Home Address *</label>
                          <AddressAutocomplete form={form} />
                          {form.touched.homeAddress &&
                          form.errors.homeAddress ? (
                            <small className="text-red-600 mt-1 text-xs md:text-sm">
                              {String(
                                form.errors.homeAddress ||
                                  "Please select an address from the dropdown suggestions"
                              )}
                            </small>
                          ) : null}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <TextInput
                            name="city"
                            label="City *"
                            wrapperClass="mt-3"
                            placeholder="city"
                          />
                          <TextInput
                            name="state"
                            label="Province *"
                            wrapperClass="mt-3"
                            placeholder="province"
                          />
                          <TextInput
                            name="zipCode"
                            label="Postal Code *"
                            wrapperClass="mt-3"
                            placeholder="Postal Code"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="rounded-[10px] mt-4 border p-4">
                      <h3 className="text-base md:text-lg font-semibold">
                        Employer Information
                      </h3>
                      <div className="mt-3">
                        <TextInput
                          name="jobTitle"
                          label="Job Title *"
                          placeholder="Enter your Job Title"
                        />
                        <TextInput
                          name="employerName"
                          label="Employer Name *"
                          wrapperClass="mt-3"
                          placeholder="Enter your Employer Name"
                        />
                        <TextInput
                          name="employerPhoneNumber"
                          label="Employer Phone Number *"
                          wrapperClass="mt-3"
                          placeholder="Enter your phone number"
                        />
                      </div>
                    </div>

                    <div className="rounded-[10px] mt-4 border p-4">
                      <h3 className="text-base md:text-lg font-semibold">
                        Others
                      </h3>
                      <div className="mt-3">
                        <div>
                          <label>How did you hear of us?*</label>
                          <select
                            {...form.getFieldProps("others")}
                            name="others"
                            className="w-full p-2 border h-[44px] bg-white rounded-lg mt-2"
                          >
                            <option value="" disabled selected>
                              Select an option
                            </option>
                            <option value="WhatsApp group">
                              WhatsApp group
                            </option>
                            <option value="Instagram">Instagram</option>
                            <option value="Referrals">Referrals</option>
                            <option value="LinkedIn">LinkedIn</option>
                            <option value="other">Other</option>
                          </select>
                          {form.touched.others && form.errors.others ? (
                            <small className="text-red-600 mt-1 text-xs md:text-sm">
                              {String(form.errors.others)}
                            </small>
                          ) : null}

                          {(form.getFieldProps("others").value === "other" ||
                            form.getFieldProps("others").value ===
                              "WhatsApp group" ||
                            form.getFieldProps("others").value ===
                              "Referrals") && (
                            <TextInput
                              name="other"
                              wrapperClass="mt-3"
                              placeholder={
                                form.getFieldProps("others").value === "other"
                                  ? "Please specify"
                                  : form.getFieldProps("others").value ===
                                    "WhatsApp group"
                                  ? "Enter WhatsApp group name"
                                  : "Enter referral details"
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between mt-6">
                      <button
                        onClick={() => product.closeKyc(false)}
                        type="button"
                        className="px-5 py-2 border border-[#D42620] text-[#D42620] rounded-md"
                      >
                        Cancel
                      </button>

                      <Button
                        label="Proceed"
                        isLoading={handleSubmit.isLoading}
                        disabled={handleSubmit.isLoading}
                        className="px-5"
                      />
                    </div>
                  </form>
                </FormikProvider>

                <div></div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default Kyc;
